import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import pick from 'lodash/pick';

import { Cube, Web, assertPlayerComponent } from '../../players';
import { type Channel } from './user';
import appConfig from '../../../app.config';

export const DefaultChannelVolume: number = 100;

export enum EventType {
  Unknown,

  Requested,
  Downloaded,
  Started,
  Stopped,
  Unloaded,
  Rated,

  Error = 1000,
  ErrorNotFound,
  ErrorHowlLoad,
  ErrorHowlPlay
}

export interface Song {
  songId: number
  title: string
  artist: string
  genre: string
  duration: number
  persistentId: string
}

export interface Event {
  type: EventType
  when: number
  ref: number
  song?: Song | string
}

export type QueueItem = Song & {
  events: Event[]
  playing: boolean
  jingleId?: number
  playFromPosition?: number

  sourceUri?: string
  destUri?: string
  soundId?: number
}

// @todo -> local-storage
export interface Queue {
  entries: QueueItem[]
  playIndex: number
}

export type ChannelState = Channel & {
  started: boolean
  lifetime: number
  queue: Queue
  muted: boolean
  runInBackground: boolean
  lastJingleCheckTime: number
}

export interface PlayerState {
  playerType: string
  playerId: number
  playerName: string
  channels: Record<number, ChannelState>
}

export interface OrchestratorState {
  players: Record<number, PlayerState>
  stationEvents: Record<number, Event[]>
  songEvents: Record<string, Event[]>
}

export interface ChannelData {
  playerId: number
  channels: Record<number, Channel>
  isOnline: boolean
}

export interface ActionSetChannelDataPayload {
  data: Record<number, ChannelData>
}

export type ActionSetChannelData = PayloadAction<ActionSetChannelDataPayload>

export interface ChannelSelector {
  playerId: number
  channel: number
}

export interface ActionSetStationIdPayload extends ChannelSelector {
  stationId: number
}

export interface ActionStartPayload extends ChannelSelector {
}

export interface ActionSchedulePayload extends ChannelSelector {
}

export interface ActionSetVolumePayload extends ChannelSelector {
  volume: number
  defer?: boolean
}

export interface ActionMutePayload extends ChannelSelector {
}

export interface ActionUnmutePayload extends ChannelSelector {
}

export interface ActionStopPayload extends ChannelSelector {
}

export interface ActionSkipPayload extends ChannelSelector {
}

export interface ActionSetJingleIdPayload extends ChannelSelector {
  jingleId: number | undefined
  force?: boolean
}

export interface ActionCheckedForJinglesPayload extends ChannelSelector  {

}

export interface ActionQueueItemPayload extends ChannelSelector, Omit<QueueItem, 'events'> {
  atIndex: number
  playFromPosition?: number
}

export interface ActionEmitItemEventPayload extends ChannelSelector {
  index: number
  type?: EventType
}

export interface ActionMovePlayIndexPayload extends ActionEmitItemEventPayload {
}

export interface ActionRequestItemPayload extends ActionEmitItemEventPayload {
  uri: string
}

export type ActionDownloadItemPayload =
    | ActionEmitItemEventPayload & {
      uri: string
      soundId?: never
    }
    | ActionEmitItemEventPayload & {
      uri?: never
      soundId: number
    }
    | ActionEmitItemEventPayload & {
      uri: string
      soundId: number
    }

export interface ActionPlayItemPayload extends ActionEmitItemEventPayload {
  fromPosition?: number
}

export interface ActionRateItemPayload extends ActionEmitItemEventPayload {
  songId: string
}

export interface ActionStopItemPayload extends ActionEmitItemEventPayload {

}

export interface ActionUnloadItemPayload extends ActionEmitItemEventPayload {

}

export interface ActionFailItemPayload extends ActionEmitItemEventPayload {
  errorType: EventType
  reason?: string
}

export type ActionSetStationId = PayloadAction<ActionSetStationIdPayload>
export type ActionStart = PayloadAction<ActionStartPayload>
export type ActionSchedule = PayloadAction<ActionSchedulePayload>
export type ActionSetVolume = PayloadAction<ActionSetVolumePayload>
export type ActionMute = PayloadAction<ActionMutePayload>
export type ActionUnmute = PayloadAction<ActionUnmutePayload>
export type ActionStop = PayloadAction<ActionStopPayload>
export type ActionSkip = PayloadAction<ActionSkipPayload>
export type ActionSetJingleId = PayloadAction<ActionSetJingleIdPayload>
export type ActionCheckedForJingles = PayloadAction<ActionCheckedForJinglesPayload>
export type ActionMovePlayIndex = PayloadAction<ActionMovePlayIndexPayload>
export type ActionQueueItem = PayloadAction<ActionQueueItemPayload>
export type ActionEmitItemEvent = PayloadAction<ActionEmitItemEventPayload>
export type ActionRequestItem = PayloadAction<ActionRequestItemPayload>
export type ActionDownloadItem = PayloadAction<ActionDownloadItemPayload>
export type ActionPlayItem = PayloadAction<ActionPlayItemPayload>
export type ActionRateItem = PayloadAction<ActionRateItemPayload>
export type ActionStopItem = PayloadAction<ActionStopItemPayload>
export type ActionUnloadItem = PayloadAction<ActionUnloadItemPayload>
export type ActionFailItem = PayloadAction<ActionFailItemPayload>

const createChannels = (nChannels: number, runInBackground: boolean): Record<number, ChannelState> => (
  [...Array(nChannels).keys()].map(i => i + 1).reduce((o, ch) => ({
    ...o,
    [ch]: {
      channel: ch,
      started: false,
      runInBackground,
      lifetime: 0,
      lastJingleCheckTime: -1,
      stationId: undefined,
      jingleId: undefined,
      queue: {
        entries: [],
        playIndex: 0
      },
      volume: DefaultChannelVolume,
      muted: false
    }
  }), {})
);

const createPlayerState = (playerType: string, playerId: number, playerName: string, nChannels: number = 1, runInBackground: boolean = false): PlayerState => ({
  playerName,
  playerType,
  playerId,
  channels: createChannels(nChannels, runInBackground)
});

/**
 * Create web player state and its component
 */
const InternalId: number = 0xdeafbeef;
export const WebId: number = InternalId;

const createWebPlayerState = (): PlayerState =>
  createPlayerState(Web, WebId, 'Web Player', 1, true);
const createCubePlayerState = (playerId: number): PlayerState =>
  createPlayerState(Cube, playerId, 'Cube Control', 4);

export const initialState: OrchestratorState = {
  players: {
    [WebId]: createWebPlayerState()
  },
  stationEvents: {},
  songEvents: {}
};

assertPlayerComponent(Web, WebId, 1);

const initChannelState = (state: OrchestratorState, payload: ChannelSelector): void => {
  const { playerId, channel } = payload;

  // Create player state in orchestrator if it doesn't exist
  if (state.players[playerId] === undefined) {
    state.players[playerId] = createCubePlayerState(playerId);
  }

  // Create player component if it doesn't exist
  assertPlayerComponent(state.players[playerId].playerType, playerId, channel);
};

const orchestratorSlice = createSlice({
  name: 'orchestrator',
  initialState,
  reducers: {
    setChannelData (state, { payload: { data } }: ActionSetChannelData) {
      Object.values(data).forEach(player => {
        const { playerId, channels } = player;

        Object.values(channels).forEach(channelData => {
          const { channel } = channelData;

          initChannelState(state, { playerId, channel });

          state.players[playerId].channels[channel] = {
            ...state.players[playerId].channels[channel],
            ...channelData
          };
        });
      });
    },

    setStationId (state, { payload }: ActionSetStationId) {
      const { playerId, channel, stationId } = payload;
      initChannelState(state, payload);

      //
      // Set station ID to undefined when set twice which is basically
      // like stopping it, that's why we set it to undefined here.
      //
      state.players[playerId].channels[channel].stationId = (
        state.players[playerId].channels[channel].stationId === stationId
          ? undefined
          : stationId
      );

      //
      // Starting/Stopping a station will always stop the currently
      // playing item, so unset the jingle ID here too
      //
      state.players[playerId].channels[channel].jingleId = undefined;
    },

    setJingleId (state, { payload }: ActionSetJingleId) {
      const { playerId, channel, jingleId, force } = payload;
      initChannelState(state, payload);

      //
      // Set jingle ID to undefined when set twice which is basically
      // like stopping it, that's why we set it to undefined here.
      //
      state.players[playerId].channels[channel].jingleId = (
        state.players[playerId].channels[channel].jingleId === jingleId
          ? undefined
          : jingleId
      );

      state.players[playerId].channels[channel].jingleForce = force ?? false;
    },

    checkedForJingles (state, { payload }: ActionCheckedForJingles) {
      const { playerId, channel } = payload;
      initChannelState(state, payload);
      state.players[playerId].channels[channel].lastJingleCheckTime = +new Date();
    },

    setVolume (state, { payload }: ActionSetVolume) {
      const { playerId, channel, volume } = payload;
      initChannelState(state, payload);
      state.players[playerId].channels[channel].volume = volume;
    },

    start (state, { payload }: ActionStart) {
      const { playerId, channel } = payload;
      initChannelState(state, payload);
      if (!state.players[playerId].channels[channel].started) {
        state.players[playerId].channels[channel].started = true;
        state.players[playerId].channels[channel].lifetime = 1;
      }
    },

    schedule (state, { payload }: ActionSchedule) {
      initChannelState(state, payload);
    },

    stop (state, { payload }: ActionStop) {
      const { playerId, channel } = payload;
      initChannelState(state, payload);
      state.players[playerId].channels[channel].started = false;
      state.players[playerId].channels[channel].lifetime = 0;
    },

    skip (state, { payload }: ActionSkip) {
      initChannelState(state, payload);
    },

    mute (state, { payload }: ActionMute) {
      const { playerId, channel } = payload;
      initChannelState(state, payload);
      state.players[playerId].channels[channel].muted = true;
    },

    unmute (state, { payload }: ActionUnmute) {
      const { playerId, channel } = payload;
      initChannelState(state, payload);
      state.players[playerId].channels[channel].muted = false;
    },

    movePlayIndex (state, { payload }: ActionMovePlayIndex) {
      const { playerId, channel, index } = payload;
      initChannelState(state, payload);
      state.players[playerId].channels[channel].queue.playIndex = index;
    },

    queueItem (state, { payload }: ActionQueueItem) {
      const { playerId, channel, atIndex, ...item } = payload;
      initChannelState(state, payload);
      if(atIndex < 0) {
        state.players[playerId].channels[channel].queue.entries.push({
          ...item,
          events: []
        });
      } else {
        state.players[playerId].channels[channel].queue.entries.splice(
          atIndex, 0, {
          ...item,
          events: []
        });
      }
    },

    requestItem (state, { payload }: ActionRequestItem) {
      const { playerId, channel, index } = payload;
      initChannelState(state, payload);

      const { queue, stationId } = state.players[playerId].channels[channel];
      const entry = queue.entries[index];

      entry.sourceUri ??= payload.uri;
      entry.destUri ??= payload.uri;

      entry.events.push({
        type: EventType.Requested,
        ref: stationId!,
        when: +new Date()
      });
    },

    downloadItem (state, { payload }: ActionDownloadItem) {
      const { playerId, channel, index } = payload;
      initChannelState(state, payload);

      const { queue, stationId } = state.players[playerId].channels[channel];
      const entry = queue.entries[index];

      if (payload.soundId !== undefined) {
        entry.soundId = payload.soundId;
      }
      if (payload.uri !== undefined) {
        entry.destUri = payload.uri;
      }

      entry.events.push({
        type: EventType.Downloaded,
        ref: stationId!,
        when: +new Date()
      });
    },

    emitItemEvent (state, { payload }: ActionEmitItemEvent) {
      const { playerId, channel, type, index } = payload;
      initChannelState(state, payload);

      const { queue, stationId } = state.players[playerId].channels[channel];
      const entry = queue.entries[index];

      entry.events.push({
        type: type ?? EventType.Unknown,
        ref: stationId!,
        when: +new Date()
      });
    },

    playItem (state, { payload }: ActionPlayItem) {
      const { playerId, channel, index } = payload;
      initChannelState(state, payload);

      const { players, stationEvents } = state;
      const { queue, stationId } = players[playerId].channels[channel];
      queue.playIndex = index;

      const entry = queue.entries[index];
      entry.playing = true;

      const event = {
        type: EventType.Started,
        ref: stationId!,
        when: +new Date()
      };

      entry.events.push(event);

      if (stationId) {
        if (typeof stationEvents[stationId] === 'undefined') {
          stationEvents[stationId] = [];
        }
        stationEvents[stationId].push({
          ...event,
          song: pick(entry, [
            'songId',
            'title',
            'artist',
            'genre',
            'duration',
            'persistentId'
          ])
        })
      }

      Object.entries(state.stationEvents).forEach(([ stationIdStr, events ]) => {
        const stationId = parseInt(stationIdStr);
        state.stationEvents[stationId] = events
          .filter(event => ((+new Date() - event.when) * 0.001) < appConfig.stationEventsMaxAgeInSeconds);
      });
    },

    rateItem (state, { payload }: ActionRateItem) {
      const { playerId, channel, songId, type } = payload;
      initChannelState(state, payload);

      const { players, songEvents } = state;
      const { stationId } = players[playerId].channels[channel];

      if(typeof songEvents[songId] === 'undefined') {
        songEvents[songId] = [];
      }
      songEvents[songId].push({
        type: EventType.Rated,
        ref: stationId!,
        when: +new Date(),
        song: songId
      });
    },

    stopItem (state, { payload }: ActionStopItem) {
      const { playerId, channel, index } = payload;
      initChannelState(state, payload);

      const { queue, stationId, jingleId } = state.players[playerId].channels[channel];
      queue.playIndex = index + 1;

      const entry = queue.entries[index];

      entry.playing = false;

      // As multiple jingle playbacks might overlap only unset the playing one
      if(entry.jingleId !== undefined && entry.jingleId === jingleId) {
        state.players[playerId].channels[channel].jingleId = undefined;
      }

      entry.events.push({
        type: EventType.Stopped,
        ref: stationId!,
        when: +new Date()
      });
    },

    unloadItem (state, { payload }: ActionUnloadItem) {
      const { playerId, channel, index } = payload;
      initChannelState(state, payload);

      const { queue, stationId } = state.players[playerId].channels[channel];
      const entry = queue.entries[index];

      entry.events.push({
        type: EventType.Unloaded,
        ref: stationId!,
        when: +new Date()
      });
    },

    failItem (state, { payload }: ActionFailItem) {
      const { playerId, channel, index, errorType } = payload;
      initChannelState(state, payload);

      const { queue, stationId } = state.players[playerId].channels[channel];
      const entry = queue.entries[index];

      entry.playing = false;

      entry.events.push({
        type: errorType,
        ref: stationId!,
        when: +new Date()
      });
    }
  }
});

export const {
  setChannelData,
  setStationId,
  setJingleId,
  checkedForJingles,
  setVolume,
  mute,
  unmute,
  start,
  schedule,
  stop,
  skip,
  movePlayIndex,
  queueItem,
  requestItem,
  downloadItem,
  emitItemEvent,
  playItem,
  rateItem,
  stopItem,
  unloadItem,
  failItem
} = orchestratorSlice.actions;

export default orchestratorSlice.reducer;
